import React from 'react';

import { TocEntry } from '../../utils/documentToc';

interface DocumentTocSidebarProps {
  toc: TocEntry[];
  onSelectSection: (sectionId: string) => void;
  onSelectHeading: (sectionId: string, headingId: string) => void;
}

/**
 * "On this page" navigator shared by the peer-review Full Document tab and the
 * Final Review document editor. Sections are the primary entries; the ADR
 * section additionally lists each ADR as a sub-item. Scroll behaviour is
 * supplied by the parent (per-tab anchors) via the two callbacks.
 */
export const DocumentTocSidebar: React.FC<DocumentTocSidebarProps> = ({
  toc,
  onSelectSection,
  onSelectHeading,
}) => (
  <nav
    aria-label="Document sections"
    className="w-full flex-shrink-0 lg:sticky lg:top-6 lg:max-h-[80vh] lg:w-64 lg:overflow-y-auto"
  >
    <div className="rounded-lg border border-base-300 bg-base-100 p-3">
      <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-base-content/50">
        On this page
      </p>
      <ul className="space-y-1">
        {toc.map((entry) => (
          <li key={entry.sectionId}>
            <button
              type="button"
              onClick={() => onSelectSection(entry.sectionId)}
              className="group flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm font-medium text-base-content/90 hover:bg-base-200"
              title={entry.title}
            >
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/70 group-hover:bg-primary" />
              <span className="truncate">{entry.title}</span>
            </button>
            {entry.subHeadings.length > 0 && (
              <ul className="ml-2 mt-0.5 space-y-0.5 border-l border-base-300 pl-3">
                {entry.subHeadings.map((sub) => (
                  <li key={sub.id}>
                    <button
                      type="button"
                      onClick={() => onSelectHeading(entry.sectionId, sub.id)}
                      className="group flex w-full items-center gap-2 rounded px-1.5 py-0.5 text-left text-xs text-base-content/60 hover:bg-base-200 hover:text-base-content"
                      title={sub.text}
                    >
                      <span className="h-1 w-1 flex-shrink-0 rounded-full bg-base-content/30 group-hover:bg-base-content/60" />
                      <span className="truncate">{sub.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  </nav>
);
